<script lang="ts">
	import { user_data } from 'src/stores/account/user';
	import { getProfileAvatarText, profiles } from 'src/stores/features/profiles';
	import { Text, CircleAvatar, Button, ButtonV2, toast } from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	import { isEmailUnverified } from 'src/helpers/email_verification_helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { handleUserProfileName, handleUserProfileAvatar } from 'src/services/http';
	import type { HomeAvatar } from 'src/types/Home';
	import EditNameAvatarModal from 'src/components/Avatar/EditNameAvatarModal.svelte';
	import LogoutModal from 'src/components/Profiles/LogoutModal.svelte';
	import { AvatarColorVariant } from '@Shelly/shelly-design-system/helpers/enums';
	import ConfirmEmail from 'src/components/ConfirmEmail.svelte';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
	import { handleLogout } from 'src/helpers/account_helpers';
	import { resetDrawer } from 'src/helpers/drawer_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import { globalLoaderMsg, showGlobalLoader } from 'src/stores/ui/global-loader';
	import { homes } from 'src/stores/account/home';

	$: activeProfile = $profiles.find((item) => item.isSelected);
	$: allowAvatarUpload = Object.keys($user_data?.available_units ?? {}).length > 0;
	$: isSharedHomeActive = isActiveHomeShared($homes.list);

	const getAvatarSrc = (activeProfile) => {
		const avatar = activeProfile?.acc_avatar?.image;
		return avatar;
	};

	let showEditModal = false;
	let showLogoutModal = false;
	let accountName = '';
	let accountAvatar: HomeAvatar = {};
	let isSavingProfile = false;

	const openEditModal = (): void => {
		if (isSharedHomeActive) return;
		accountName = activeProfile?.title ?? '';
		accountAvatar = { ...(activeProfile?.acc_avatar ?? {}) };
		showEditModal = true;
	};

	const openLogoutModal = (): void => {
		showLogoutModal = true;
	};

	const onConfirmLogout = async (): Promise<void> => {
		showLogoutModal = false;
		resetDrawer('login');
		$showGlobalLoader = true;
		$globalLoaderMsg = $format('loggin_out_profile_loading_msg', {
			default: 'Logging out from profile...',
		});
		await handleLogout();
		$showGlobalLoader = false;
		$globalLoaderMsg = '';
	};

	const onSaveProfile = async (
		e: CustomEvent<{ name: string; avatar: HomeAvatar; shouldClose: boolean }>,
	): Promise<void> => {
		if (!activeProfile) return;
		const { name, avatar, shouldClose } = e.detail;
		isSavingProfile = true;

		try {
			let ok = true;
			const profileAvatar = {
				...avatar,
				text: getProfileAvatarText(name, activeProfile.email),
			};

			if (name && name !== activeProfile.title) {
				ok = (await handleUserProfileName(name)) && ok;
			}

			const avatarChanged =
				JSON.stringify(profileAvatar) !== JSON.stringify(activeProfile.acc_avatar ?? {});
			if (avatarChanged) {
				ok = (await handleUserProfileAvatar(profileAvatar)) && ok;
			}

			if (!ok) {
				throw new Error('save failed');
			}

			profiles.edit({ ...activeProfile, title: name, acc_avatar: profileAvatar });

			toast.open({
				type: 'success',
				title: $format('settings_profile_updated', { default: 'Profile updated successfully' }),
			});
			if (shouldClose) {
				showEditModal = false;
			}
		} catch {
			toast.open({
				type: 'danger',
				title: $format('setting_err_toast', { default: 'Failed to apply settings.' }),
			});
		} finally {
			isSavingProfile = false;
		}
	};
</script>

<div class="d-flex flex-direction-column">
	<div class="d-flex align-items-center">
		{#if activeProfile?.acc_avatar}
			<!-- handle case where activeProfile.acc_avatar is defined -->
			<CircleAvatar
				slot="trigger"
				size="l"
				class="mr-m"
				text={getProfileAvatarText(activeProfile?.title, activeProfile?.email)}
				image={getAvatarSrc(activeProfile)}
				backgroundColor={activeProfile?.acc_avatar?.backgroundColor ??
					AvatarColorVariant.COLOR_PRIMARY}
				borderStatus={{
					color: 'color-white',
				}}
			/>
		{:else}
			<!-- handle case where activeProfile is undefined -->
			<CircleAvatar
				icon="fal fa-user"
				size="l"
				class="mr-m"
				backgroundColor={AvatarColorVariant.COLOR_PRIMARY}
				borderStatus={{ color: 'color-white' }}
			/>
		{/if}

		<div class="d-flex flex-direction-column flex-1 min-width-0">
			<!-- handle case where activeProfile.title is defined -->
			{#if activeProfile?.title}
				<Text class="width-full mb-s" element="h3" styleType="h2" noWrap>
					{activeProfile?.title}
				</Text>
			{/if}
			<Text class="width-full text-color-muted" element="p" styleType="body-small" noWrap>
				{$user_data.email}
			</Text>
		</div>
	</div>

	<div class="d-flex flex-wrap gap-s mt-m">
		{#if $showNewLayout}
			<ButtonV2
				variant="primary"
				size="m"
				icon="fas fa-pen"
				leftIconSize="xs"
				aria-label={$format('edit_profile', { default: 'Edit profile' })}
				disabled={isSharedHomeActive}
				on:click={openEditModal}
			>
				{$format('edit_profile', { default: 'Edit profile' })}
			</ButtonV2>
			<ButtonV2
				variant="secondary"
				size="m"
				icon="fas fa-right-from-bracket"
				leftIconSize="xs"
				aria-label={capFirstChar(
					$format('label_profile_logout', { default: 'Log out from profile' }),
				)}
				on:click={openLogoutModal}
			>
				{capFirstChar($format('label_profile_logout', { default: 'Log out from profile' }))}
			</ButtonV2>
		{:else}
			<Button
				class="width-md-auto width-sm-full"
				variant="secondary"
				size="m"
				icon="fas fa-pen"
				aria-label={$format('edit_profile', { default: 'Edit profile' })}
				disabled={isSharedHomeActive}
				on:click={openEditModal}
			>
				{$format('edit_profile', { default: 'Edit profile' })}
			</Button>
		{/if}
	</div>
</div>

<EditNameAvatarModal
	showModal={showEditModal}
	type="account"
	title={$format('edit_profile', { default: 'Edit profile' })}
	nameLabel={$format('label_profile_name', { default: 'Profile name' })}
	nameInputId="edit-account-name-input"
	bind:name={accountName}
	bind:avatar={accountAvatar}
	isPremium={!!$user_data?.premium}
	allowUpload={allowAvatarUpload}
	loading={isSavingProfile}
	on:save={onSaveProfile}
	on:close={() => (showEditModal = false)}
/>

<LogoutModal bind:showModal={showLogoutModal} on:logout={onConfirmLogout} />

{#if $isEmailUnverified}
	<div class="mt-s">
		<ConfirmEmail />
	</div>
{/if}
